import { PORT_MESSAGE } from '@server/account-field-format';
import type { WarehouseProblem } from '@server/warehouse-failure';
import { deriveAccountKeyFromDims, deriveConnectionKey } from '@server/warehouse-keys';
/**
 * PlatformAccountStep — phase 3b: create the warehouse account in RudderStack.
 *
 * The form is rendered from the account definition's schema (`settings.warehouses.getPlatformAccountForm`),
 * and "Validate and create" sends it to RudderStack as the signed-in user; RudderStack validates the
 * credential before it stores anything. A failed validation shows the failed step and keeps every
 * typed value mounted, so the admin fixes the warehouse (or a field) and retries.
 *
 * Authentication modes of this type that are NOT enabled for RudderStack create (Redshift IAM, for
 * example) stay reachable: they hand off to the existing local wizard (LA4, decision 9).
 *
 * **An existing RudderStack account comes first (ACT2-1031).** When the workspace already holds
 * accounts of this type that no Lookout connection uses, a select at the top offers them before the
 * create form: connecting one (`adoptAccountAsNewConnection`, the same call as Settings › RudderStack
 * accounts › Connect) needs no credential typed here. Nothing is preselected — the header button
 * names the account it would connect, and the create form appears only once "Create a new account"
 * is chosen. Not offered while reconnecting, which re-binds a paused connection instead.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Label } from '@/_shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import {
  ACCOUNT_DRAFTS_CLEARED_EVENT,
  accountDraftKey,
  clearUncertainName,
  readUncertainAttempt,
  saveUncertainName,
  submittedDraftCleanup,
  type UncertainAttempt,
} from '@/_shared/lib/account-drafts';
import { hasOrpcCode, orpcErrorMessage } from '@/_shared/lib/orpc-errors';
import { isRudderstackReauthRequired } from '@/_shared/lib/rudderstack-reauth';

import { CLICKHOUSE_SETUP_DOC_URL } from '../../../lib/warehouse-setup-guides';
import { ExternalDocLink } from '../../ExternalDocLink';
import { AccountForm, type AccountFormHandle } from '../../rudderstackAccount/AccountForm';
import { comparableAccountName, fieldFormatError, fieldKeyOf } from '../../rudderstackAccount/accountFormSchema';
import { SettingsCardsSkeleton } from '../../SettingsCardsSkeleton';
import { refreshPermissionOnRefusal } from '../accountCreatePermission';
import { toastAdopted } from '../adopt/adoptedToast';
import { accountRowIdentity } from '../adopt/adoptPresentation';
import type { RsAccountOverview } from '../adopt/useAdoptAccounts';
import { RudderstackSignInButton } from '../PermissionNotice';
import {
  isGatewayError,
  refusalMessage,
  refusalProblem,
  UNMAPPED_CREATE_ERROR_COPY,
  withoutPlatformName,
} from '../refusalCopy';
import { FailedStepCallout } from '../shared/FailedStepCallout';
import type { WarehouseAccount } from '../useWarehouseAccounts';
import { connectableRsAccounts } from '../warehouseList';
import { canonicalWarehouseTypeKey, type WarehouseTypeMeta } from '../warehouseTypes';
import { ClickHouseSetupPanel } from './ClickHouseSetupPanel';
import type { SetupView } from './SnowflakeSetupStep';
import { localAlternativesFor } from './useAddAccountWizard';

export interface FailedStep {
  id: number;
  name: string;
  success: boolean;
  error: string;
  /** The step described for a person (ACT2-1077). */
  problem?: WarehouseProblem;
}

export interface PlatformSubmitHandle {
  submit: () => Promise<void>;
}

interface PlatformAccountStepProps {
  blockedReason: string | null;
  type: WarehouseTypeMeta;
  onCreated: () => void;
  onBusyChange: (busy: boolean) => void;
  /** Hand an unflagged authentication mode to the existing local wizard. */
  onUseLocal: () => void;
  /** A platform-generated mode (Snowflake) started or resumed a setup: show its key and SQL. */
  onSetupStarted: (setup: SetupView) => void;
  /** The drawer's header label: "Connect …", "Generate key" for a platform-generated mode, else "Validate and create". */
  onSubmitLabel: (label: string) => void;
  /** False while an existing account could be connected but nothing has been chosen yet. */
  onCanSubmitChange?: (canSubmit: boolean) => void;
  /** Phase 3c: Reconnect — re-bind this paused connection in place instead of creating a new one. */
  reconnectConnectionId?: string;
  /** Phase 3c: the paused connection's key — only a setup for these coordinates may be resumed. */
  reconnectConnectionKey?: string;
  /** The workspace's connections, so the accounts offered are exactly the Warehouses list's Available. */
  connections: readonly WarehouseAccount[];
}

/** The select's value for "Create a new account" — never a RudderStack account id. */
const NEW_ACCOUNT = '__new__';

/** The UI matches the platform exact-name check after trimming. */
const NAME_TAKEN_COPY = 'An account with this name already exists. Choose another name.';

/** The connection key the server derives for a ClickHouse create: type, normalised host and database. */
function attemptConnectionKey(values: Record<string, string>): string | null {
  const accountKey = deriveAccountKeyFromDims('CLICKHOUSE', values['host'] ?? '', values['database'] ?? '');
  return accountKey ? deriveConnectionKey(accountKey) : null;
}

/** What an unchanged retry repeats: the connection key, the user and the port. The server key omits the port. */
function attemptCoords(values: Record<string, string>): string | null {
  const key = attemptConnectionKey(values);
  return key === null ? null : JSON.stringify([key, (values['user'] ?? '').trim(), (values['port'] ?? '').trim()]);
}

/** "ClickHouse analytics", then "ClickHouse analytics 2" and on, until no account has the name. */
function freeAccountName(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(comparableAccountName(base))) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base} ${n}`;
    if (!taken.has(comparableAccountName(candidate))) return candidate;
  }
}

/**
 * Create failures a resend can fix: the platform failed or timed out, or the account was created but
 * not recorded. A resend with the same values links an account the first attempt created.
 */
const RETRYABLE_CREATE_REASONS: ReadonlySet<string> = new Set([
  'rudderstack_error',
  'rudderstack_not_found',
  'validator_unavailable',
  'validator_timeout',
  'client_timeout',
  'record_failed',
]);

interface RpcErrorShape {
  data?: {
    failedStep?: FailedStep;
    reason?: string;
    message?: string;
    field?: string;
    existingConnectionName?: string;
    existingAccountName?: string;
    existingConnectionId?: string;
  };
}

export const PlatformAccountStep = React.forwardRef<PlatformSubmitHandle, PlatformAccountStepProps>(
  function PlatformAccountStep(
    {
      blockedReason,
      type,
      onCreated,
      onBusyChange,
      onUseLocal,
      onSetupStarted,
      onSubmitLabel,
      onCanSubmitChange,
      reconnectConnectionId,
      reconnectConnectionKey,
      connections,
    },
    ref,
  ) {
    const orpc = useOrpc();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const { platformAccountCreateFamilies, workspace } = useWorkspace();
    const { data, isLoading, error, refetch } = useQuery(
      orpc.settings.warehouses.getPlatformAccountForm.queryOptions({ input: { type: type.key } }),
    );
    const modes = data?.modes ?? [];
    // Accounts of this type that one Connect can turn into a connection: the Warehouses list's
    // Available rule, read from the same function.
    const overview = useQuery({
      ...orpc.settings.warehouses.listRsAccountsOverview.queryOptions(),
      enabled: !reconnectConnectionId,
    });
    const candidates = connectableRsAccounts(
      connections,
      (overview.data as { accounts?: RsAccountOverview[] } | undefined)?.accounts ?? [],
    ).connectable.filter((a) => canonicalWarehouseTypeKey(a.type) === type.key);
    /** '' = nothing chosen yet, NEW_ACCOUNT = create one, else the chosen account id. */
    const [choice, setChoice] = React.useState('');
    const offerExisting = !reconnectConnectionId && candidates.length > 0;
    const effectiveChoice = offerExisting ? choice : NEW_ACCOUNT;
    // Once the create form has been shown, keep it: a refetch that brings an eligible account must not
    // unmount the form under the admin's typed values.
    const overviewSettled = !overview.isLoading;
    React.useEffect(() => {
      if (overviewSettled && !offerExisting && choice === '') setChoice(NEW_ACCOUNT);
    }, [overviewSettled, offerExisting, choice]);
    const isClickHouse = type.key === 'CLICKHOUSE';
    const draftKey =
      isClickHouse && !reconnectConnectionId && user ? accountDraftKey(user.id, workspace.id, type.key) : undefined;
    // The name of a create that ended uncertain. It rides in the draft entry, so it survives a closed
    // drawer. The in-memory value belongs to one draft key and covers a name that must not be stored.
    // The user can load after the first render, so the stored name is read once the draft key exists.
    const [settledName, setSettledName] = React.useState<{
      key: string | undefined;
      value: UncertainAttempt | null;
    } | null>(null);
    const uncertainAttempt =
      settledName && settledName.key === draftKey
        ? settledName.value
        : draftKey
          ? readUncertainAttempt(draftKey)
          : null;
    const uncertainName = uncertainAttempt?.name ?? null;
    const uncertainRef = React.useRef<UncertainAttempt | null>(null);
    uncertainRef.current = uncertainAttempt;
    const draftsAllowed = React.useRef(true);
    const mounted = React.useRef(true);
    React.useEffect(() => {
      mounted.current = true;
      const stop = () => {
        draftsAllowed.current = false;
        setSettledName({ key: draftKey, value: null });
      };
      window.addEventListener(ACCOUNT_DRAFTS_CLEARED_EVENT, stop);
      return () => {
        mounted.current = false;
        window.removeEventListener(ACCOUNT_DRAFTS_CLEARED_EVENT, stop);
      };
    }, [draftKey]);
    const rememberUncertainName = (attempt: UncertainAttempt) => {
      // A callback of an unmounted step must not replace the exemption a newer mount holds.
      if (!mounted.current && draftKey && readUncertainAttempt(draftKey) !== null) return;
      setSettledName({ key: draftKey, value: attempt });
      if (draftKey && draftsAllowed.current) saveUncertainName(draftKey, attempt);
    };
    /** A definitive answer for `name` ends its exemption; the stored name goes only if it still equals `name`. */
    const settleUncertainName = (name: string | null) => {
      if (name === null) return;
      if (draftKey) clearUncertainName(draftKey, name);
      if (uncertainRef.current?.name === name) setSettledName({ key: draftKey, value: null });
    };
    const overviewAccounts = (overview.data as { accounts?: RsAccountOverview[] } | undefined)?.accounts;
    const takenNames = React.useMemo(
      () => new Set((overviewAccounts ?? []).flatMap((a) => (a.name === null ? [] : [comparableAccountName(a.name)]))),
      [overviewAccounts],
    );
    /** The name check skips the name of an uncertain create: the server adopts it or answers name_conflict. */
    const checkedNames = React.useMemo(() => {
      if (uncertainName === null) return takenNames;
      const skip = comparableAccountName(uncertainName);
      return new Set([...takenNames].filter((name) => name !== skip));
    }, [takenNames, uncertainName]);
    const [modeIndex, setModeIndex] = React.useState(0);
    const mode = modes[modeIndex];
    const suggestName = React.useCallback(
      (v: Record<string, string>) => {
        const database = (v['database'] ?? '').trim();
        // The definition's own rule for the database field decides whether the name can carry it.
        const databaseField = mode?.fields.find((f) => fieldKeyOf(f) === 'database');
        const usable =
          database !== '' && databaseField !== undefined && fieldFormatError(database, databaseField, '') === null;
        const base = usable ? `${type.label} ${database}` : type.label;
        return freeAccountName(base, checkedNames);
      },
      [type.label, checkedNames, mode],
    );
    const chosenAccount = candidates.find((a) => a.accountId === effectiveChoice) ?? null;
    const chosenName = chosenAccount ? accountRowIdentity(chosenAccount).title : null;
    const formRef = React.useRef<AccountFormHandle>(null);
    const [failedStep, setFailedStep] = React.useState<FailedStep | null>(null);
    const [formError, setFormErrorText] = React.useState<string | null>(null);
    // ACT2-1077: a refusal the server described keeps its support id, code and details.
    const [formProblem, setFormProblem] = React.useState<WarehouseProblem | null>(null);
    const setFormError = React.useCallback((text: string | null) => {
      setFormErrorText(text);
      setFormProblem(null);
    }, []);
    /** Show a refusal: the server's problem when it sent one, else its sentence. */
    const showRefusal = React.useCallback((err: unknown, fallback: string) => {
      const problem = refusalProblem(err);
      setFormErrorText(problem ? null : refusalMessage(err, fallback));
      setFormProblem(problem ?? null);
    }, []);
    const [retryable, setRetryable] = React.useState(false);
    const [retryValuesChanged, setRetryValuesChanged] = React.useState(false);
    const onFormEdit = React.useCallback(() => {
      if (retryable) setRetryValuesChanged(true);
      setRetryable(false);
    }, [retryable]);
    /** The error is one the admin fixes in the form, so the connection-settings docs help. */
    const [fixableInForm, setFixableInForm] = React.useState(false);
    /** A field shows an error: a format the admin fixes in the form. */
    const [fieldErrors, setFieldErrors] = React.useState(false);
    /** Set when an existing account was bound and the typed secret was therefore not used (D-b). */
    const [adoptedNotice, setAdoptedNotice] = React.useState<string | null>(null);
    /** The notice tells the admin to change the credential in RudderStack, so it carries the way there. */
    const [noticeLinksToRudderstack, setNoticeLinksToRudderstack] = React.useState(false);
    /** Set when the RudderStack sign-in expired: the form stays, with a way back in. */
    const [reauthRequired, setReauthRequired] = React.useState(false);
    /** ClickHouse only: the three names the setup SQL panel renders, followed as the admin types. */
    const [clickHouseNames, setClickHouseNames] = React.useState({ database: '', user: '' });
    const onFormValues = React.useCallback((v: Record<string, string>) => {
      setClickHouseNames((prev) => {
        const next = {
          database: v['database'] ?? '',
          user: v['user'] ?? '',
        };
        return prev.database === next.database && prev.user === next.user ? prev : next;
      });
    }, []);
    // Not while reconnecting: the paused connection's credential is held in RudderStack, and the server
    // refuses a local save onto it (`updateConnection`'s CONFLICT), so a local method would only fail.
    const localAlternatives = reconnectConnectionId ? [] : localAlternativesFor(type, platformAccountCreateFamilies);
    const isPlatformKey = mode?.credentialSource === 'platform';
    const setups = useQuery({
      ...orpc.settings.warehouses.listAccountSetups.queryOptions({ input: { type: type.key } }),
      enabled: isPlatformKey,
    });
    // While reconnecting, a setup for other coordinates is not this connection's: resuming it would
    // have RudderStack create that account before the server refuses the rebind. The server lists only
    // this workspace's setups, so the connection-key suffix of the mark is enough to tell them apart
    // (the server re-checks it before verifying).
    const resumableSetups = (setups.data ?? []).filter(
      (s) =>
        reconnectConnectionKey === undefined || (s.externalId ?? '').endsWith(`:connection:${reconnectConnectionKey}`),
    );

    const handleFormError = (
      err: unknown,
      fallback: string,
      creatingAccount = false,
      submitted: Record<string, string> | null = null,
    ) => {
      refreshPermissionOnRefusal(queryClient, orpc, err);
      const submittedName = submitted?.['name'] ?? null;
      const e = err as RpcErrorShape;
      setRetryable(false);
      setFixableInForm(false);
      if (isRudderstackReauthRequired(err)) {
        setReauthRequired(true);
        return;
      }
      if (
        isClickHouse &&
        formRef.current?.getRawValues()['database']?.trim().toLowerCase() === '_rudderstack' &&
        /CH_CONFIG_INVALID\s*\(workingDatabase\)/u.test(e.data?.failedStep?.error ?? e.data?.message ?? '')
      ) {
        formRef.current?.setError(
          'database',
          'Choose a database other than _rudderstack, which RudderStack uses to store sync state.',
        );
        setFixableInForm(true);
        return;
      }
      if (e.data?.failedStep) {
        setFailedStep(e.data.failedStep);
        setFixableInForm(true);
        return;
      }
      // By code, not text: the drawer checks names only against the warehouse accounts it lists.
      if (e.data?.reason === 'name_conflict') {
        settleUncertainName(submittedName);
        formRef.current?.setError('name', NAME_TAKEN_COPY);
        return;
      }
      if (e.data?.reason === 'connection_exists') {
        const existing =
          e.data.existingConnectionName ??
          e.data.existingAccountName ??
          connections.find((connection) => connection.id === e.data?.existingConnectionId)?.name;
        setFormError(
          existing
            ? `“${existing}” already connects to this warehouse. Close this form and open it in Settings › Warehouses.`
            : 'A connection for this warehouse already exists. Close this form and find the matching host and database in Settings › Warehouses.',
        );
        return;
      }
      if (
        (e.data?.reason === 'invalid_config' || e.data?.reason === 'invalid_request' || e.data?.field !== undefined) &&
        typeof e.data?.message === 'string' &&
        e.data.message !== ''
      ) {
        let messages: string[] = [e.data.message];
        try {
          const parsed: unknown = JSON.parse(e.data.message);
          messages = Array.isArray(parsed)
            ? parsed.filter((entry): entry is string => typeof entry === 'string')
            : messages;
        } catch {
          /* A plain sentence is the normal server response. */
        }
        let mapped = false;
        for (const message of messages) {
          const field = mode?.fields.find((field) =>
            e.data?.field
              ? fieldKeyOf(field) === e.data.field
              : [fieldKeyOf(field), field.label].some(
                  (label) =>
                    message.toLowerCase().startsWith(`${label.toLowerCase()} `) ||
                    message.toLowerCase().startsWith(`${label.toLowerCase()}:`),
                ),
          );
          if (!field) continue;
          const key = fieldKeyOf(field);
          const copy =
            key === 'port'
              ? PORT_MESSAGE
              : /^[{[]/u.test(message.trim())
                ? `Check ${field.label.toLowerCase()} and try again.`
                : message.startsWith(`${field.label}: `)
                  ? message.slice(field.label.length + 2)
                  : message;
          formRef.current?.setError(key, copy);
          mapped = true;
        }
        setFixableInForm(true);
        if (!mapped) {
          const problem = refusalProblem(err);
          const text = /^[{[]/u.test(e.data.message.trim())
            ? 'The account settings were not accepted. Check each field and try again.'
            : withoutPlatformName(e.data.message);
          if (problem) setFormProblem({ ...problem, details: text });
          else setFormError(text);
        }
        return;
      }
      const reason = e.data?.reason;
      if (reason === 'rudderstack_rejected') setFixableInForm(true);
      const uncertain =
        creatingAccount &&
        ((typeof reason === 'string' && RETRYABLE_CREATE_REASONS.has(reason)) ||
          (reason === undefined && (hasOrpcCode(err, 'INTERNAL_SERVER_ERROR') || isGatewayError(err))));
      setRetryable(uncertain);
      // The create may have succeeded remotely. A refetch then lists the submitted name; the retry must
      // not refuse it, so the server's adoption path can link the account it already made.
      if (uncertain && submittedName !== null) {
        const coords = attemptCoords(submitted ?? {});
        rememberUncertainName(coords === null ? { name: submittedName } : { name: submittedName, coords });
      }
      const problem = refusalProblem(err);
      setFormErrorText(problem ? null : refusalMessage(err, fallback, { creatingAccount }));
      // config-backend's field text is shown beside the headline, so it loses the platform name too.
      setFormProblem(
        problem?.details ? { ...problem, details: withoutPlatformName(problem.details) } : (problem ?? null),
      );
    };

    /**
     * After an uncertain create, `connection_exists` means the first attempt made this connection, but
     * only for an unchanged retry: the server derives that error from type, host and database. The
     * refetched overview must then list an account with the attempt's name and connection key.
     */
    const createdByEarlierAttempt = async (err: unknown, values: Record<string, string>): Promise<boolean> => {
      const name = values['name'];
      const attempt = uncertainRef.current;
      if (name === undefined || attempt?.name !== name || attempt.coords === undefined) return false;
      if ((err as RpcErrorShape).data?.reason !== 'connection_exists') return false;
      if (attemptCoords(values) !== attempt.coords) return false;
      const connectionKey = attemptConnectionKey(values);
      if (connectionKey === null) return false;
      const fresh = await overview.refetch();
      if (fresh.isError || !fresh.isSuccess) return false;
      const accounts = (fresh.data as { accounts?: RsAccountOverview[] } | undefined)?.accounts ?? [];
      return accounts.some(
        (a) =>
          a.name !== null &&
          comparableAccountName(a.name) === comparableAccountName(name) &&
          a.type === 'CLICKHOUSE' &&
          a.connectionKey === connectionKey &&
          a.state !== 'paused' &&
          a.connectionId !== null,
      );
    };

    const create = useMutation(
      orpc.settings.warehouses.createPlatformAccount.mutationOptions({
        onMutate: () => ({
          clearSubmittedDraft: draftKey ? submittedDraftCleanup(draftKey) : () => false,
        }),
        onSuccess: (result, variables, context) => {
          // This also runs after unmount, but only removes the values this request submitted.
          if (context?.clearSubmittedDraft()) formRef.current?.clearDraft();
          settleUncertainName(variables.values['name'] ?? null);
          void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
          toast.success(
            !result.adopted
              ? 'Account created'
              : result.secretIgnored
                ? 'An existing account was linked. The secret you typed was not used.'
                : // Adopted, but the account came out of this same form (an earlier attempt whose
                  // record or response was lost), so it holds what that attempt sent. Usually the
                  // same secret, typed again — which is why "the secret you typed was not used" and
                  // its revoke-it instruction would be wrong here. It still says whose credential the
                  // account holds, because Lookout cannot tell whether this attempt changed it.
                  'The account an earlier attempt created was linked. It keeps the credential that attempt sent.',
          );
          // Decision 4: the account this connection kept when it was deleted was bound again.
          if (result.retained) {
            setAdoptedNotice(
              'Reconnected to the account kept when this connection was deleted. It keeps its own settings and credential; to change them, rotate the key (Snowflake) or revoke it and create the account again.',
            );
            return;
          }
          // Shown as well as toasted: the account keeps its stored credential, so the admin needs the
          // instruction to stay on screen.
          if (result.secretIgnored) {
            setAdoptedNotice(
              type.key === 'SNOWFLAKE'
                ? 'An account for these coordinates already existed, so it was linked to this connection. It keeps its stored credential and the secret you typed was not used — to change that credential, rotate its key from Settings › Warehouses, or revoke it there and create it again.'
                : // "Revoke it and create it again" is not a path we can promise here: config-backend
                  // refuses to delete an account a source, destination or data graph still uses, so
                  // for an account in use that advice ends in a refusal. Lookout has no edit path of
                  // its own either (ACT2-839) — Settings › Accounts covers activation destinations
                  // only — so the change is made on the account in RudderStack, from the Reverse ETL
                  // source that uses it. Revoke stays available for an account nothing uses.
                  'An account for these coordinates already existed, so it was linked to this connection. It keeps its stored credential and the secret you typed was not used. To change that credential, edit the account in your workspace from the Reverse ETL source that uses it. Lookout cannot change it here. If no source uses the account, revoke it from Settings › Warehouses and create it again.',
            );
            setNoticeLinksToRudderstack(type.key !== 'SNOWFLAKE');
            return;
          }
          onCreated();
        },
        // Reconnect re-binds an existing connection, so the create copy and its retry do not apply.
        onError: async (err, variables, context) => {
          if (await createdByEarlierAttempt(err, variables.values)) {
            if (context?.clearSubmittedDraft()) formRef.current?.clearDraft();
            settleUncertainName(variables.values['name'] ?? null);
            void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
            toast.success('A connection for these details already exists and is linked.');
            onCreated();
            return;
          }
          handleFormError(
            err,
            reconnectConnectionId
              ? 'Lookout could not reconnect this connection. Try again.'
              : UNMAPPED_CREATE_ERROR_COPY,
            !reconnectConnectionId,
            variables.values,
          );
        },
      }),
    );

    const connectExisting = useMutation(
      orpc.settings.warehouses.adoptAccountAsNewConnection.mutationOptions({
        onSuccess: (result) => {
          void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
          // ACT2-1130: the `_rudderstack` write check, shown as Test connection shows it.
          toastAdopted('Connection created. Queries will use it within a few minutes.', result);
          onCreated();
        },
        onError: (err) => {
          refreshPermissionOnRefusal(queryClient, orpc, err);
          if (isRudderstackReauthRequired(err)) {
            setReauthRequired(true);
            return;
          }
          showRefusal(err, 'Could not connect this account');
        },
      }),
    );

    const start = useMutation(
      orpc.settings.warehouses.startAccountSetup.mutationOptions({
        onSuccess: (result) => {
          // `adopted`: a verify whose response was lost had already created the account, so there is
          // no new setup to run — the connection is recorded and the drawer is done.
          if (result.outcome === 'adopted') {
            void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
            toast.success('The account an earlier attempt created was linked.');
            onCreated();
            return;
          }
          onSetupStarted(result.setup);
        },
        onError: (err) => handleFormError(err, 'Lookout could not start the key setup. Try again.'),
      }),
    );

    React.useEffect(() => {
      onSubmitLabel(
        chosenName !== null
          ? `Connect ${chosenName}`
          : effectiveChoice === ''
            ? 'Continue'
            : isPlatformKey
              ? 'Generate key'
              : 'Validate and create',
      );
    }, [chosenName, effectiveChoice, isPlatformKey, onSubmitLabel]);

    React.useEffect(() => {
      onCanSubmitChange?.(effectiveChoice !== '');
    }, [effectiveChoice, onCanSubmitChange]);

    React.useEffect(() => {
      onBusyChange(create.isPending || start.isPending || connectExisting.isPending);
    }, [create.isPending, start.isPending, connectExisting.isPending, onBusyChange]);

    const submit = React.useCallback(async () => {
      if (blockedReason !== null || create.isPending || start.isPending || connectExisting.isPending) return;
      setRetryable(false);
      setRetryValuesChanged(false);
      if (chosenAccount) {
        setFormError(null);
        setReauthRequired(false);
        connectExisting.mutate({ accountId: chosenAccount.accountId });
        return;
      }
      if (effectiveChoice === '') return;
      if (!mode || !formRef.current || !(await formRef.current.validate())) return;
      setFailedStep(null);
      setFormError(null);
      setReauthRequired(false);
      // Raw values, not the shared `assemblePayload` output: that trims every field, and a
      // password or a private key may legitimately begin or end with whitespace. Options are
      // trimmed here (as they always were); a secret is sent exactly as typed, and the server
      // trims options again in `splitPlatformValues`.
      const raw = formRef.current.getRawValues();
      const secretKeys = new Set(mode.secretKeys);
      const values = Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, secretKeys.has(key) ? value : value.trim()]),
      );
      const request = {
        type: type.key,
        authenticationType: mode.authenticationType,
        values,
        ...(reconnectConnectionId ? { reconnectConnectionId } : {}),
      };
      if (isPlatformKey) start.mutate(request);
      else create.mutate(request);
    }, [
      blockedReason,
      mode,
      create,
      start,
      connectExisting,
      setFormError,
      chosenAccount,
      effectiveChoice,
      isPlatformKey,
      type.key,
      reconnectConnectionId,
    ]);
    React.useImperativeHandle(ref, () => ({ submit }), [submit]);

    const errorRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      if (!formError && !formProblem && !failedStep && !reauthRequired) return;
      errorRef.current?.scrollIntoView({ block: 'nearest' });
      errorRef.current?.focus({ preventScroll: true });
    }, [formError, formProblem, failedStep, reauthRequired]);

    const alternatives =
      localAlternatives.length > 0 ? (
        <Callout variant="info">
          <p>Other authentication methods keep the credential in Lookout:</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {localAlternatives.map((alt) => (
              <Button key={alt.family} type="button" variant="secondary" onClick={onUseLocal} label={alt.label} />
            ))}
          </div>
        </Callout>
      ) : null;

    // Wait for the account list too: the create form must not appear and then vanish under the admin
    // when existing accounts arrive.
    if (isLoading || (overview.isLoading && !reconnectConnectionId)) return <SettingsCardsSkeleton />;
    // A failed background refetch keeps the form the user is filling in; only a first load shows the error.
    if (error && !data) {
      return (
        <div className="grid gap-4">
          {isRudderstackReauthRequired(error) ? (
            <Callout variant="warning">
              <p>Your RudderStack sign-in expired. Sign in again to continue.</p>
              <div className="mt-2">
                <RudderstackSignInButton />
              </div>
            </Callout>
          ) : (
            <Callout variant="error">
              <p>
                {orpcErrorMessage(
                  error,
                  'Lookout could not load the account form. Try again. If it keeps failing, contact our team.',
                )}
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  label="Try again"
                  onClick={() => {
                    refreshPermissionOnRefusal(queryClient, orpc, error);
                    void refetch();
                  }}
                />
              </div>
            </Callout>
          )}
          {alternatives}
        </div>
      );
    }
    if (!mode || !data) {
      return (
        <div className="grid gap-4">
          <Callout variant="error">This warehouse type cannot be created from Lookout yet.</Callout>
          {alternatives}
        </div>
      );
    }
    const existingSelect = offerExisting ? (
      <div className="grid gap-1.5">
        <Label htmlFor="platform-account-choice">Existing account</Label>
        <Select
          value={choice}
          onValueChange={(v) => {
            setFailedStep(null);
            setFormError(null);
            setRetryable(false);
            setChoice(v);
          }}
        >
          <SelectTrigger id="platform-account-choice">
            <SelectValue placeholder="Choose an existing account, or create one" />
          </SelectTrigger>
          {/* `popper`, not the default `item-aligned`: the select starts with no value, and item-aligned
              positioning then placed the list off-screen, so it opened looking empty. */}
          <SelectContent
            position="popper"
            className="w-(--radix-select-trigger-width)"
            onCloseAutoFocus={(event) => {
              // The new form already focused its first field. Returning to the picker would blur
              // that pristine field and immediately show a required-name error.
              if (choice === NEW_ACCOUNT) event.preventDefault();
            }}
          >
            {candidates.map((a) => {
              const identity = accountRowIdentity(a);
              return (
                <SelectItem key={a.accountId} value={a.accountId} disabled={!a.connectable}>
                  {identity.title}
                  {identity.detail !== null ? ` · ${identity.detail}` : ''}
                  {a.connectable ? '' : ' (not available yet)'}
                </SelectItem>
              );
            })}
            <SelectItem value={NEW_ACCOUNT}>Create a new account</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {candidates.length === 1
            ? 'An account of this type already exists'
            : `${candidates.length} accounts of this type already exist`}
          . Connecting one needs no credentials here.
        </p>
      </div>
    ) : null;
    const showCreate = effectiveChoice === NEW_ACCOUNT;
    const hasError = failedStep !== null || formError !== null || formProblem !== null;
    // A field error or an expired sign-in is an error on screen too: the setup docs line steps aside.
    const errorShown = hasError || fieldErrors || reauthRequired;

    return (
      <div className="grid gap-4">
        {existingSelect}
        {(hasError || reauthRequired) && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role={formProblem || failedStep?.problem ? 'group' : 'alert'}
            aria-label="Account creation error"
            className="outline-none"
          >
            {failedStep && <FailedStepCallout step={failedStep} />}
            {formError && <Callout variant="error">{formError}</Callout>}
            {formProblem && <WarehouseProblemCallout problem={formProblem} />}

            {reauthRequired && (
              <Callout variant="error">
                <p>
                  Your RudderStack sign-in has expired, so the account could not be created. Sign in again and retry.{' '}
                  {isClickHouse && !reconnectConnectionId
                    ? 'Enter the password again after signing in.'
                    : 'What you typed here is not kept.'}
                </p>
                <div className="mt-2">
                  <RudderstackSignInButton />
                </div>
              </Callout>
            )}
          </div>
        )}
        {isClickHouse && ((hasError && (fixableInForm || retryable)) || (showCreate && fieldErrors)) && (
          <div className="flex flex-wrap items-center gap-3">
            {retryable && (
              <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={blockedReason !== null}
                  onClick={() => void submit()}
                  loading={create.isPending}
                  label="Try again"
                />
              </ActionTooltip>
            )}
            {(fixableInForm || (showCreate && fieldErrors)) && (
              <p className="text-sm text-muted-foreground">
                For help with these fields, see the{' '}
                <ExternalDocLink href={`${CLICKHOUSE_SETUP_DOC_URL}#connection-settings`}>
                  connection settings
                </ExternalDocLink>
                .
              </p>
            )}
            {fixableInForm && failedStep && (
              <p className="text-sm text-muted-foreground">
                For more help, see the{' '}
                <ExternalDocLink href={`${CLICKHOUSE_SETUP_DOC_URL}#error-codes`}>
                  ClickHouse error codes
                </ExternalDocLink>
                .
              </p>
            )}
          </div>
        )}
        {retryValuesChanged && (
          <p role="status" className="text-sm text-muted-foreground">
            The values changed. Validate again to check the updated settings.
          </p>
        )}
        {hasError && !isClickHouse && retryable && (
          <div>
            <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''}>
              <Button
                type="button"
                variant="secondary"
                disabled={blockedReason !== null}
                onClick={() => void submit()}
                loading={create.isPending}
                label="Try again"
              />
            </ActionTooltip>
          </div>
        )}
        {adoptedNotice !== null && (
          <Callout variant="warning">
            <p>{adoptedNotice}</p>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="secondary" onClick={onCreated} label="Done" />
              {noticeLinksToRudderstack && data?.editCredentialInRudderstackUrl !== undefined && (
                <Button variant="secondary" asChild>
                  <a href={data.editCredentialInRudderstackUrl} target="_blank" rel="noopener noreferrer">
                    Open sources
                  </a>
                </Button>
              )}
            </div>
          </Callout>
        )}
        {showCreate &&
          isPlatformKey &&
          resumableSetups.map((s) => (
            <Callout key={s.id} variant="highlight">
              <p>
                Setup “{s.name}” is waiting for its SQL to be run (valid until {new Date(s.expiresAt).toLocaleString()}
                ).
              </p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onSetupStarted(s)}
                  label={`Resume ${s.name}`}
                />
              </div>
            </Callout>
          ))}
        {showCreate &&
          (isPlatformKey ? (
            <Callout variant="highlight">
              RudderStack generates a key pair and shows you the public key and setup SQL. Lookout never receives the
              private key.
            </Callout>
          ) : isClickHouse ? (
            // Leo: point to the docs instead of a note, and never beside an error.
            errorShown ? null : (
              <p className="text-sm">
                <ExternalDocLink href={CLICKHOUSE_SETUP_DOC_URL}>How to set up ClickHouse</ExternalDocLink>
              </p>
            )
          ) : (
            <Callout variant="highlight">
              Create the _rudderstack schema and give this user write access to it first. The secret is stored in
              RudderStack, not Lookout.
            </Callout>
          ))}
        {showCreate && modes.length > 1 && (
          <Select
            value={String(modeIndex)}
            onValueChange={(v) => {
              setFailedStep(null);
              setFormError(null);
              setRetryable(false);
              setModeIndex(Number(v));
            }}
          >
            <SelectTrigger aria-label="Authentication">
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              onCloseAutoFocus={(event) => {
                // The new mode already focused its first field; returning here would validate it on blur.
                event.preventDefault();
              }}
            >
              {modes.map((m, i) => (
                <SelectItem key={m.family} value={String(i)}>
                  {m.authenticationType ?? m.family}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {showCreate && (
          <fieldset disabled={create.isPending || start.isPending} className="grid min-w-0 gap-4">
            <AccountForm
              key={`${user?.id}:${workspace.id}:${mode.family}`}
              ref={formRef}
              autoFocus
              pending={create.isPending || start.isPending}
              onSubmit={submit}
              onEdit={onFormEdit}
              onStartOver={() => {
                setFailedStep(null);
                setFormError(null);
                setReauthRequired(false);
                setRetryable(false);
                setFixableInForm(false);
                setRetryValuesChanged(false);
              }}
              draftKey={draftKey}
              fields={mode.fields}
              secretFields={mode.secretKeys}
              definitionName={data.accountDefinitionName}
              onValuesChange={onFormValues}
              onHasErrorsChange={setFieldErrors}
              {...(isClickHouse && !reconnectConnectionId
                ? { suggestName, takenNames: checkedNames, takenNameMessage: NAME_TAKEN_COPY }
                : {})}
            />
          </fieldset>
        )}
        {showCreate && isClickHouse && <ClickHouseSetupPanel values={clickHouseNames} />}
        {showCreate && alternatives}
      </div>
    );
  },
);
